import {useState} from "react";
import {
    Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, Typography, List, ListItem, ListItemText,
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {useTranslation} from "react-i18next";

interface DeleteTemplateDialogProps {
    open: boolean;
    templateNames: string[];
    onClose: () => void;
    onConfirm: () => void;
    loading?: boolean;
}

const MAX_VISIBLE = 5;

const DeleteTemplateDialog = ({open, templateNames, onClose, onConfirm, loading}: DeleteTemplateDialogProps) => {
    const {t} = useTranslation("templates");
    const sorted = [...templateNames].sort((a, b) => a.localeCompare(b));
    const isBulk = sorted.length > 1;
    const [expanded, setExpanded] = useState(false);

    const visibleNames = expanded ? sorted : sorted.slice(0, MAX_VISIBLE);
    const hiddenCount = sorted.length - MAX_VISIBLE;

    const handleClose = () => {
        setExpanded(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                {isBulk
                    ? t('deleteDialog.titleBulk', {count: sorted.length})
                    : t('deleteDialog.title')}
                <IconButton size="small" onClick={handleClose}><Close/></IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2">
                    {isBulk ? t('deleteDialog.messageBulk') : t('deleteDialog.message')}
                </Typography>
                <List dense sx={{maxHeight: 300, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, mt: 1.5, py: 0.5}}>
                    {visibleNames.map((name) => (
                        <ListItem key={name} sx={{py: 0.25, px: 1.5}}>
                            <ListItemText
                                primary={name}
                                primaryTypographyProps={{variant: 'body2', fontFamily: 'monospace', fontSize: '0.8rem'}}
                            />
                        </ListItem>
                    ))}
                </List>
                {!expanded && hiddenCount > 0 && (
                    <Chip
                        label={`+${hiddenCount}`}
                        size="small"
                        onClick={() => setExpanded(true)}
                        sx={{mt: 1, cursor: 'pointer', fontWeight: 600}}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button variant="outlined" onClick={handleClose}>{t('deleteDialog.cancel')}</Button>
                <Button variant="contained" color="accent" onClick={onConfirm} disabled={loading}>
                    {loading ? t('deleteDialog.loading') : t('deleteDialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteTemplateDialog;
